import NaicsSelector from "../../../shared/components/NaicsSelector";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";

import api from "../../../services/api/axios";
import SectorDropdown from "../../../shared/forms/SectorDropDown";
import { useAuth } from "../../../providers/AuthProvider";
import BackButton from "../../../shared/components/BackButton";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import { INCUBATOR_PIPELINE_OPTIONS } from "./pipelineOptions";
import IncubatorLeaseDocumentsTab from "./IncubatorLeaseDocumentsTab";
import {
  formatBroadSectorLabel,
} from "../companies/BRECompanyHelpers";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const toNullIfBlank = (value) =>
  value === "" || value === undefined || value === null ? null : value;

const asIntOrNull = (value) => {
  const parsed = parseInt(String(value ?? "").replace(/[, ]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const asFloatOrNull = (value) => {
  const parsed = parseFloat(String(value ?? "").replace(/[, ]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const PIPELINE_CONFIG = {
  "Prospect-Active":                    { bg: "#FFF8E1", color: "#E65100",  border: "#FFB300" },
  "Prospect-Inactive":                  { bg: "#F5F5F5", color: "#616161",  border: "#9E9E9E" },
  "Active-Early Stage":                 { bg: "#E3F2FD", color: "#0D47A1",  border: "#1976D2" },
  "Active-Growth Stage":                { bg: "#E8F5E9", color: "#2E7D32",  border: "#43A047" },
  "Graduated-Moved Within the Landing": { bg: "#EDE9FE", color: "#5B21B6",  border: "#7C3AED" },
  "Graduated-Left Region":              { bg: "#F3E8FF", color: "#6D28D9",  border: "#8B5CF6" },
  "Moved/Relocated":                    { bg: "#FEF3C7", color: "#92400E",  border: "#D97706" },
  "Inactive":                           { bg: "#F5F5F5", color: "#374151",  border: "#6B7280" },
};

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

const createEmptyIncubator = () => ({
  company_name: "", address: "", zip_code: "", state: "", city: "", country: "",
  room_number: "", mailing_address: "", contact_name: "", contact_telephone: "",
  contact_email: "", website: "", industry: "", sector: "", naics_text: "", naics_code: "",
  industry_custom: "", number_of_employees: "", industries_served: "",
  geographic_markets_covered: "", annual_revenue: "", lease_expiration: "",
  facebook: "", linkedin: "", twitter: "", pipeline_status: "",
  lease_document_name: "", lease_document_uploaded_at: null, lease_documents: [],
});

const hydrateIncubatorForm = (data = {}) => ({
  ...createEmptyIncubator(), ...data,
  company_name: data.company_name || "", address: data.address || "",
  zip_code: data.zip_code || "", state: data.state || "", city: data.city || "",
  country: data.country || "", room_number: data.room_number || "",
  mailing_address: data.mailing_address || "", contact_name: data.contact_name || "",
  contact_telephone: data.contact_telephone || "", contact_email: data.contact_email || "",
  website: data.website || "", industry: data.industry || "",
  sector: data.sector || "", naics_text: data.naics_text || "", naics_code: data.naics_code || "",
  industry_custom: data.industry_custom || "",
  number_of_employees:
    data.number_of_employees != null ? String(data.number_of_employees) : "",
  industries_served: data.industries_served || "",
  geographic_markets_covered: data.geographic_markets_covered || "",
  annual_revenue:
    data.annual_revenue != null ? String(data.annual_revenue) : "",
  lease_expiration: data.lease_expiration || "",
  facebook: data.facebook || "",
  linkedin: data.linkedin || "", twitter: data.twitter || "",
  pipeline_status: data.pipeline_status || "",
  lease_document_name: data.lease_document_name || "",
  lease_document_uploaded_at: data.lease_document_uploaded_at ?? null,
  lease_documents: Array.isArray(data.lease_documents) ? data.lease_documents : [],
});

const buildIncubatorPayload = (incubator) =>
  Object.entries({
    company_name: incubator.company_name, address: incubator.address,
    zip_code: incubator.zip_code, state: incubator.state, city: incubator.city,
    country: incubator.country, room_number: incubator.room_number,
    mailing_address: incubator.mailing_address, contact_name: incubator.contact_name,
    contact_telephone: incubator.contact_telephone, contact_email: incubator.contact_email,
    website: incubator.website,
    industry:
      incubator.industry_custom && incubator.industry_custom.trim() !== ""
        ? incubator.industry_custom.trim()
        : incubator.naics_text && incubator.naics_text.trim() !== ""
          ? incubator.naics_text.trim()
          : incubator.industry && incubator.industry.trim() !== ""
            ? incubator.industry.trim()
            : null,
    sector: incubator.sector,
    naics_text: incubator.naics_text,
    naics_code: incubator.naics_code,
    industry_custom: incubator.industry_custom,
    number_of_employees: asIntOrNull(incubator.number_of_employees),
    industries_served: incubator.industries_served,
    geographic_markets_covered: incubator.geographic_markets_covered,
    annual_revenue: asFloatOrNull(incubator.annual_revenue),
    lease_expiration: toNullIfBlank(incubator.lease_expiration),
    facebook: incubator.facebook,
    linkedin: incubator.linkedin, twitter: incubator.twitter,
    pipeline_status: incubator.pipeline_status,
  }).reduce((acc, [key, value]) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    acc[key] = trimmed === "" ? null : trimmed;
    return acc;
  }, {});

function formatErrorMessage(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return "Failed to save incubator. Please try again.";
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      const loc = Array.isArray(item.loc) ? item.loc.join(".") : item.loc || "field";
      return `${loc}: ${item.msg}`;
    }).join("\n");
  }
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}

function IncubatorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = window.location.pathname.endsWith("/new");
  const { token, user } = useAuth();

  const [incubator, setIncubator] = useState(createEmptyIncubator);
  const [loading, setLoading] = useState(!isNew);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message); setSnackbarSeverity(severity); setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!isNew && token) {
      api.get(`/incubators/${id}`)
        .then((res) => setIncubator(hydrateIncubatorForm(res.data)))
        .catch(() => showSnackbar("Failed to load incubator details.", "error"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id, isNew, token]);

  const handleChange = (field) => (event) =>
    setIncubator((prev) => ({ ...prev, [field]: event.target.value }));

  const handlePipelineChange = (status) => {
    const next = status === incubator.pipeline_status ? "" : status;
    setIncubator((prev) => ({ ...prev, pipeline_status: next }));
  };

  const handleSubmit = () => {
    const payload = buildIncubatorPayload(incubator);
    const request = isNew
      ? api.post("/incubators/", payload)
      : api.patch(`/incubators/${id}`, payload);
    request
      .then(() => navigate("/incubators"))
      .catch((err) => showSnackbar(formatErrorMessage(err), "error"));
  };

  const handleIncubatorUpdated = (nextIncubator) => {
    setIncubator(hydrateIncubatorForm(nextIncubator));
  };

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to="/incubators" />
              {user?.organization?.name && (
                <Chip label={user.organization.name} size="small"
                  sx={{ bgcolor: "rgba(0,51,108,0.06)", color: BRAND_BLUE, fontWeight: 600, fontSize: 12, border: "1px solid rgba(0,51,108,0.12)" }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <BusinessIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? "New Incubator" : incubator.company_name || "Untitled Incubator"}
                </Typography>
                {!isNew && (incubator.city || incubator.state) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: "#d32f2f" }} />
                    <Typography variant="body2" sx={{ color: BRAND_GREEN }}>
                      {[incubator.city, incubator.state].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Pipeline status chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Status
              </Typography>
              {INCUBATOR_PIPELINE_OPTIONS.map((status) => {
                const cfg = PIPELINE_CONFIG[status] || { bg: "#F5F5F5", color: "#374151", border: "#9E9E9E" };
                const isActive = incubator.pipeline_status === status;
                return (
                  <Chip
                    key={status}
                    label={status}
                    size="small"
                    onClick={() => handlePipelineChange(status)}
                    sx={{
                      cursor: "pointer",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 11,
                      bgcolor: isActive ? cfg.bg : "transparent",
                      color: isActive ? cfg.color : "text.secondary",
                      border: `1.5px solid ${isActive ? cfg.border : "rgba(0,0,0,0.15)"}`,
                      transition: "all 0.15s ease",
                      "&:hover": { bgcolor: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}` },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Divider />
        </Paper>

        {/* CONTENT CARD */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", p: 3, bgcolor: "white" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>

            {/* General & Business Details */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>General & Business Details</SectionHeader>
              <Stack spacing={3}>
                <TextField label="Company Name" value={incubator.company_name} onChange={handleChange("company_name")} fullWidth required />
                <TextField label="Website" value={incubator.website} onChange={handleChange("website")} fullWidth />
                <TextField
                  select
                  label="Pipeline Status"
                  value={incubator.pipeline_status || ""}
                  onChange={handleChange("pipeline_status")}
                  fullWidth
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {INCUBATOR_PIPELINE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                <SectorDropdown
                  label="Sector"
                  value={incubator.sector}
                  onChange={handleChange("sector")}

                  formatOptionLabel={formatBroadSectorLabel}
                  fullWidth
                />
                <NaicsSelector value={incubator.naics_code || ""} title={incubator.naics_text || ""}
                      onChange={(code, title) => setIncubator((prev) => ({ ...prev, naics_code: code, naics_text: title }))}
                      onSectorChange={(sector) => setIncubator((prev) => ({ ...prev, sector }))} />
                <TextField label="NAICS Text" value={incubator.naics_text} onChange={handleChange("naics_text")} fullWidth />
                <TextField label="Custom Industry" value={incubator.industry_custom} onChange={handleChange("industry_custom")} fullWidth />
                <TextField label="Number Of Employees" value={incubator.number_of_employees || ""} onChange={handleChange("number_of_employees")} type="number" fullWidth />
                <TextField label="Industries Served" value={incubator.industries_served || ""} onChange={handleChange("industries_served")} fullWidth />
                <TextField label="Geographic Markets Covered" value={incubator.geographic_markets_covered || ""} onChange={handleChange("geographic_markets_covered")} fullWidth />
                <TextField label="Annual Revenue" value={incubator.annual_revenue || ""} onChange={handleChange("annual_revenue")} type="number" fullWidth />
                <TextField
                  label="Lease Expiration"
                  value={incubator.lease_expiration || ""}
                  onChange={handleChange("lease_expiration")}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: BRAND_BLUE,
                      background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
                      boxShadow: "0 4px 14px rgba(0,51,108,0.12)",
                      "& fieldset": { border: `2px solid ${BRAND_BLUE}` },
                      "&:hover fieldset": { borderColor: BRAND_BLUE },
                      "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
                    },
                    "& .MuiInputLabel-root": { fontWeight: 600, color: BRAND_BLUE },
                    "& .MuiInputLabel-root.Mui-focused": { color: BRAND_BLUE },
                    "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(13%) sepia(63%) saturate(1200%) hue-rotate(195deg) brightness(85%)",
                      cursor: "pointer",
                    },
                  }}
                />
              </Stack>
            </Box>

            {/* Location */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Location</SectionHeader>
              <Stack spacing={3}>
                <TextField label="Country" value={incubator.country} onChange={handleChange("country")} fullWidth />
                <TextField label="State / Province" value={incubator.state} onChange={handleChange("state")} fullWidth />
                <TextField label="City" value={incubator.city} onChange={handleChange("city")} fullWidth />
                <TextField label="Zip Code" value={incubator.zip_code} onChange={handleChange("zip_code")} fullWidth />
                <TextField label="Address" value={incubator.address} onChange={handleChange("address")} fullWidth />
                <TextField label="Room Number" value={incubator.room_number} onChange={handleChange("room_number")} fullWidth />
                <TextField label="Mailing Address" value={incubator.mailing_address} onChange={handleChange("mailing_address")} fullWidth />
              </Stack>
            </Box>

            {/* Contact */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Contact</SectionHeader>
              <Stack spacing={3}>
                <TextField label="Name" value={incubator.contact_name} onChange={handleChange("contact_name")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Phone" value={incubator.contact_telephone} onChange={handleChange("contact_telephone")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Email" type="email" value={incubator.contact_email} onChange={handleChange("contact_email")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: BRAND_GREEN, fontSize: 20 }} /></InputAdornment> }}
                />
              </Stack>
            </Box>

            {/* Social Media */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Social Media</SectionHeader>
              <Stack spacing={3}>
                <TextField label="LinkedIn" value={incubator.linkedin} onChange={handleChange("linkedin")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><LinkedInIcon sx={{ color: "#0A66C2", fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Twitter / X" value={incubator.twitter} onChange={handleChange("twitter")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><TwitterIcon sx={{ color: "#1DA1F2", fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Facebook" value={incubator.facebook} onChange={handleChange("facebook")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: "#1877F2", fontSize: 20 }} /></InputAdornment> }}
                />
              </Stack>
            </Box>

          </Box>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2.5, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} sx={PRIMARY_BUTTON_SX}>
              {isNew ? "Add Incubator" : "Save Changes"}
            </Button>
          </Box>
        </Paper>

        {!isNew && (
          <Paper elevation={0} sx={{ mt: 2, borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", p: 3, bgcolor: "white" }}>
            <SectionHeader>Lease Documents</SectionHeader>
            <IncubatorLeaseDocumentsTab
              incubatorId={id}
              incubator={incubator}
              onIncubatorUpdated={handleIncubatorUpdated}
            />
          </Paper>
        )}
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default IncubatorDetails;
